import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import Headercomponent from "./Headercomponent";
import Searchcomponent from "./Searchcomponent";
import { FaFacebook, FaInstagram, FaWhatsapp, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaPills } from "react-icons/fa";
import { GiMedicines } from "react-icons/gi";
import { MdHealthAndSafety, MdLocalShipping } from "react-icons/md";
import { RiCustomerService2Fill } from "react-icons/ri";
import { BsPrescription2 } from "react-icons/bs";

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDBnzIitbmRBp1o-RpA-uYLUDr3tWtdK1Y",
  authDomain: "lihen-pharmacy.firebaseapp.com",
  projectId: "lihen-pharmacy",
  storageBucket: "lihen-pharmacy.appspot.com",
  messagingSenderId: "546291727469",
  appId: "1:546291727469:web:3b1aaff3f11e0877e2d364",
  measurementId: "G-YXF7FF6CR9",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const HomePage = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Categories to match Firestore collections
  const categories = [
    "Allergies",
    "Analgesics",
    "AntiInfectives",
    "Antimalaria",
    "Cardio",
    "Consumables",
    "Contraceptives",
    "Cough",
    "Creams",
    "Diabetics",
    "Eye",
    "Gastro",
    "Haematinics",
    "Multivitamins",
    "Paediatrics",
    "Supplements",
  ];

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Fetch products from Firestore
  const fetchProducts = async (queryText) => {
    setIsLoading(true);
    setError(null);
    const results = [];

    try {
      const lowercaseQuery = queryText.toLowerCase().trim(); // Convert to lowercase and trim spaces

      for (const category of categories) {
        const categoryCollection = collection(db, category);
        const q = query(
          categoryCollection,
          where("ID", ">=", lowercaseQuery),
          where("ID", "<=", lowercaseQuery + "\uf8ff")
        );
        const querySnapshot = await getDocs(q);

        querySnapshot.forEach((doc) => {
          const productData = doc.data();
          results.push({
            id: doc.id, // Correctly access the document ID
            category,
            ID: productData.ID,
            Price: productData.Price,
            description: productData.description,
            image: productData.image,
          });
        });
      }
      setSearchResults(results);
    } catch (error) {
      console.error("Error fetching products: ", error);
      setError(`Failed to fetch products: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  const debouncedFetchProducts = debounce(fetchProducts, 300);

  // Clear search results
  const handleClearSearch = () => {
    setSearchResults([]);
  };

  // Navigate to product details page
  const handleProductClick = (productId, category) => {
    navigate(`/product/${category}/${productId}`);
  };

  // Navigate to drugs page
  const handleNavigateToDrugs = () => {
    navigate("/drugs");
  };

  return (
    <div className="bg-gray-50 text-gray-800 min-h-screen font-sans">
      <Headercomponent />

      {/* Search Bar */}
      <Searchcomponent
        onSearch={debouncedFetchProducts}
        onClear={handleClearSearch}
        error={error}
        searchResults={searchResults}
        onProductClick={handleProductClick}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 text-white py-20 md:py-28 text-center overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Empowering Your <br className="hidden md:block" />
            <span className="text-emerald-300">Health & Wellness</span>
          </h2>
          <p className="mt-6 text-lg lg:text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
            Discover a trusted partner in health with exceptional services and
            high-quality products tailored for you.
          </p>
          <button
            className="mt-10 bg-white text-green-700 font-semibold py-4 px-10 rounded-full shadow-lg hover:bg-emerald-50 hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-50"
            onClick={handleNavigateToDrugs}
          >
            Shop Medications
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-gray-800">About Lihen Pharmacy</h3>
            <div className="h-1 w-24 bg-green-600 mx-auto mb-10"></div>
            <p className="text-lg text-gray-600 leading-relaxed">
              At Lihen Pharmacy, we are dedicated to providing exceptional healthcare with
              personalized solutions. Our team of experienced professionals is committed to 
              offering top-quality pharmaceutical products and unmatched customer support, 
              ensuring your health and wellness are always our top priority.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-800">Our Services</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Prescription Medications",
                description: "Professional fulfillment of your prescriptions with care and accuracy.",
                icon: <BsPrescription2 className="text-4xl text-green-600" />,
              },
              {
                title: "Wellness Products",
                description: "Comprehensive range of health and wellness products for your needs.",
                icon: <MdHealthAndSafety className="text-4xl text-green-600" />,
              },
              {
                title: "Health Consultations",
                description: "Expert advice from qualified healthcare professionals.",
                icon: <RiCustomerService2Fill className="text-4xl text-green-600" />,
              },
              {
                title: "Health Supplements",
                description: "Premium supplements to support your health goals.",
                icon: <GiMedicines className="text-4xl text-green-600" />,
              },
              {
                title: "Vitamins & Minerals",
                description: "Essential vitamins and minerals for optimal health.",
                icon: <FaPills className="text-4xl text-green-600" />,
              },
              {
                title: "Fast Delivery",
                description: "Reliable and secure delivery to your doorstep.",
                icon: <MdLocalShipping className="text-4xl text-green-600" />,
              },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex justify-center mb-6">
                  {service.icon}
                </div>
                <h4 className="text-xl font-semibold text-center mb-4 text-gray-800">{service.title}</h4>
                <p className="text-gray-600 text-center">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-800">Contact Us</h3>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            We're here to assist you with all your healthcare needs. Reach out to us through any of the channels below.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-center border border-gray-100">
              <div className="flex justify-center mb-4">
                <FaMapMarkerAlt className="text-3xl text-green-600" />
              </div>
              <h4 className="text-xl font-semibold mb-4 text-gray-800">Our Address</h4>
              <p className="text-gray-600">
                Vessel of Honor Complex, <br />
                Ekhei Girls Road, Auchi, Edo State
              </p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-center border border-gray-100">
              <div className="flex justify-center mb-4">
                <FaPhoneAlt className="text-3xl text-green-600" />
              </div>
              <h4 className="text-xl font-semibold mb-4 text-gray-800">Call Us</h4>
              <p className="text-gray-600">+234 913 928 1851</p>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 text-center border border-gray-100">
              <div className="flex justify-center mb-4">
                <FaEnvelope className="text-3xl text-green-600" />
              </div>
              <h4 className="text-xl font-semibold mb-4 text-gray-800">Email Us</h4>
              <p className="text-gray-600">contact@lihenpharmacy.com</p>
            </div>
          </div>
          <div className="text-center">
            <h4 className="text-2xl font-semibold mb-6 text-gray-800">Connect With Us</h4>
            <div className="flex justify-center space-x-6">
              <a
                href="https://instagram.com/lihenpharmacy"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-100 p-4 rounded-full text-gray-700 hover:bg-green-600 hover:text-white transition-all duration-300 transform hover:scale-110"
                aria-label="Instagram"
              >
                <FaInstagram className="text-2xl" />
              </a>
              <a
                href="https://web.facebook.com/profile.php?id=极5765792903"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-100 p-4 rounded-full text-gray-700 hover:bg-blue-600 hover:text-white transition-all duration-300 transform hover:scale-110"
                aria-label="Facebook"
              >
                <FaFacebook className="text-2xl" />
              </a>
              <a
                href="https://wa.me/+2349139281851"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-100 p-4 rounded-full text-gray极700 hover:bg-green-600 hover:text-white transition-all duration-300 transform hover:scale-110"
                aria-label="WhatsApp"
              >
                <FaWhatsapp className="text-2xl" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400">&copy; {new Date().getFullYear()} Lihen Pharmacy. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;